import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, CheckCircle, AlertTriangle, AlertCircle, X } from 'lucide-react';
import {
  announcementsService,
  AnnouncementType,
  ANNOUNCEMENT_TYPE_BANNER,
} from '../services/announcementsService';
import { AnnouncementText } from './AnnouncementText';

const TYPE_ICON: Record<AnnouncementType, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

/** Kolik novinek se vejde do pruhu, nez zbytek pocka na odbyti tech prvnich. */
const STROP = 4;

/**
 * Zobrazí NEPŘEČTENÉ novinky (seřazené dle priority) nahoře na dashboardu.
 * Křížkem si uživatel novinku odbyde – z pruhu zmizí, ale zůstane v přehledu
 * novinek. Nic nevykreslí, pokud žádné nepřečtené nejsou.
 */
export const AnnouncementsBanner: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: announcements = [] } = useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => announcementsService.getActiveAnnouncements(),
  });

  const readMutation = useMutation({
    mutationFn: (id: string) => announcementsService.markRead(id),
    // Prekresli i box s nahledem a prehled - vsude se meni pocet neprectenych.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  });

  if (announcements.length === 0) return null;

  const sorted = [...announcements].sort((a, b) =>
    b.priority !== a.priority
      ? b.priority - a.priority
      : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="mb-6 space-y-3">
      {sorted.slice(0, STROP).map((a) => {
        const Icon = TYPE_ICON[a.type];
        return (
          <div key={a.id} className={`border rounded-lg p-4 flex items-start gap-3 ${ANNOUNCEMENT_TYPE_BANNER[a.type]}`}>
            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold break-words">{a.title}</h4>
              <AnnouncementText text={a.content} radku={4} />
            </div>
            <button
              onClick={() => readMutation.mutate(a.id)}
              disabled={readMutation.isPending}
              title="Označit jako přečtené (novinka zůstane v přehledu novinek)"
              className="flex-shrink-0 p-1 -m-1 rounded opacity-60 hover:opacity-100 hover:bg-black/5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
